import { useState, useCallback, useMemo } from 'react';
import classnames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { createUseStyles, useTheme } from 'react-jss';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

import {
  PanelSubscriptionSettings,
  updateSubscriptionFormSettings,
  tagsByPanelIdSelector,
  panelSubscriptionSettingByPanelIdSelector,
} from 'store';
import { Theme } from '@app/theme';
import {
  CenteredModal,
  SharableLink,
  Input,
  SubmitButton,
  SquareButton,
  CreatableTagsAutocomplete,
  CheckboxesToArrayField,
} from 'components';
import { useFormikDispatchSubmit } from 'hooks/useFormikSubmit';
import { clientSpecialConfigs } from 'config/clientSpecialConfigs';
import { useParticipantFields } from 'queries';

const useStyles = createUseStyles((theme: Theme) => ({
  linkContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.marginBase * 2,
    width: '100%',
  },
  link: {
    flex: 1,
    marginRight: theme.marginBase,
  },
  settings: {
    maxHeight: 1000,
    transition: 'max-height .3s',
  },
  settingsClosed: {
    maxHeight: 0,
    overflow: 'hidden',
  },
  buttonsLine: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.marginBase * 4,
  },
  spacer: {
    flex: 1,
  },
  settingButton: {
    ...theme.fonts.label,
    display: 'flex',
    alignItems: 'center',
    color: theme.colors.blueAccessible,
    padding: 0,
    margin: 0,
  },
  caret: {
    fontSize: theme.iconSize.regular,
    transition: 'transform .3s',
    transform: 'rotate(0)',
    marginLeft: theme.marginBase / 2,
  },
  caretClosed: {
    transform: 'rotate(180deg)',
  },
}));

const styles = {
  menuList: (base: any) => ({
    ...base,
    maxHeight: 170,
  }),
};

interface Props {
  isOpen: boolean;
  onRequestClose?: () => any;
  panelId?: string;
}

type Values = Partial<PanelSubscriptionSettings>;

const validationSchema = Yup.object().shape({
  title: Yup.string().nullable(),
  logoUrl: Yup.string().url().nullable(),
  fields: Yup.array(Yup.string()),
});

export const PanelSubscriptionFormModal = ({
  isOpen,
  onRequestClose,
  panelId,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();
  const settings = useSelector((state: any) =>
    panelSubscriptionSettingByPanelIdSelector(state, panelId),
  );
  const tags = useSelector((state: any) =>
    tagsByPanelIdSelector(state, panelId),
  );

  const { data: participantFields } = useParticipantFields();

  const options = useMemo(() => {
    if (!participantFields) {
      return [];
    }

    return participantFields
      .filter(
        (f) =>
          f.customForm?.available === undefined ||
          f.customForm?.available === true,
      )
      .map((f) => ({
        label: f.label,
        value: f.name,
        required: f.customForm?.required,
      }));
  }, [participantFields]);

  const [settingOpened, setSettingOpened] = useState(false);
  const toggleSettingOpened = useCallback(
    () => setSettingOpened(!settingOpened),
    [setSettingOpened, settingOpened],
  );

  const thunk = useCallback(
    (values: Values) => {
      if (!settings) {
        return;
      }
      return updateSubscriptionFormSettings(settings.id, {
        title: values.title || null,
        logoUrl: values.logoUrl || null,
        tags: values.tags || [],
        fields: values.fields || [],
      });
    },
    [settings],
  );
  const onSuccess = useCallback(
    () => setSettingOpened(false),
    [setSettingOpened],
  );

  const settingsToValues = useCallback(
    (settings?: PanelSubscriptionSettings): Values => {
      const defaultValues: Values = {
        title: t('anonymous.panel.subscribe.title'),
        logoUrl: '',
        tags: [],
        fields: ['firstName', 'lastName', 'email', 'phoneNumber'],
      };

      return {
        ...defaultValues,
        ...settings,
        tags: (settings && settings.tags) || defaultValues.tags,
      };
    },
    [t],
  );

  const submit = useFormikDispatchSubmit<Values>(thunk, {
    updateValues: settingsToValues,
    onSuccess,
  });

  const initialValues: Values = useMemo(
    () => settingsToValues(settings),
    [settings],
  );

  const clientConfig = clientSpecialConfigs.find(
    ({ panelId }: { panelId?: string }) =>
      settings && settings.panelId === panelId,
  );
  const clientSpecialFields =
    clientConfig &&
    clientConfig.specialFields.map((field) => ({
      value: field.name,
      label: field.participantLabel,
    }));

  if (!settings) {
    return null;
  }

  return (
    <CenteredModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      title={t('participants.list.subscribeForm.title')}
    >
      <div>
        <div className={classes.linkContainer}>
          <SharableLink
            className={classes.link}
            link={`https://${window.location.host}/anonymous/subscribe-panel/${settings.id}`}
          />
          <SquareButton
            icon="ri-eye-line"
            to={`/anonymous/subscribe-panel/${settings.id}?preview=true`}
            target="_blank"
            color="blue"
          />
        </div>
        <div className={classnames(classes.buttonsLine)}>
          <button
            className={classes.settingButton}
            onClick={toggleSettingOpened}
          >
            <Trans i18nKey="participants.list.subscribeForm.setting" />
            <i
              className={classnames('ri-arrow-up-s-line', classes.caret, {
                [classes.caretClosed]: !settingOpened,
              })}
            />
          </button>
        </div>
        <Formik
          onSubmit={submit}
          initialValues={initialValues}
          validationSchema={validationSchema}
        >
          <Form
            className={classnames(classes.settings, {
              [classes.settingsClosed]: !settingOpened,
            })}
          >
            <Input
              label={t('participants.list.subscribeForm.pageTitle')}
              placeholder={t('anonymous.panel.subscribe.title')}
              name="title"
            />

            <Input
              label={t('participants.list.subscribeForm.logoUrl.label')}
              secondaryLabel={t(
                'participants.list.subscribeForm.logoUrl.subLabel',
              )}
              name="logoUrl"
              placeholder={t(
                'participants.list.subscribeForm.logoUrl.placeholder',
              )}
            />

            <CreatableTagsAutocomplete
              label={t('participants.list.subscribeForm.tags.label')}
              secondaryLabel={t(
                'participants.list.subscribeForm.tags.subLabel',
              )}
              name="tags"
              isMulti
              placeholder={t(
                'participants.list.subscribeForm.tags.placeholder',
              )}
              options={tags}
              styles={styles}
            />

            <CheckboxesToArrayField
              label={t('participants.list.subscribeForm.fields.label')}
              secondaryLabel={t(
                'participants.list.subscribeForm.fields.subLabel',
              )}
              options={
                clientSpecialFields && clientSpecialFields.length
                  ? [...options, ...clientSpecialFields]
                  : options
              }
              name="fields"
            />

            <div className={classnames(classes.buttonsLine)}>
              <div className={classes.spacer} />
              <SubmitButton small color="blue" variant="muted">
                <Trans i18nKey="participants.list.subscribeForm.save" />
              </SubmitButton>
            </div>
          </Form>
        </Formik>
      </div>
    </CenteredModal>
  );
};
